<template>
  <master-layout no-login>
    <div
      :class="[
        'scanner-page flex h-full flex-col',
        showCheckInSuccess
          ? 'bg-scanner-success text-white'
          : showScannerSurface
            ? 'bg-transparent'
            : 'bg-brown-50',
      ]"
    >
      <div
        v-if="showScannerSurface && !showCheckInSuccess"
        :class="['fixed inset-0 z-[9999]', isWebScannerFallback ? 'bg-black' : 'bg-transparent']"
      >
        <div
          class="pointer-events-none absolute left-1/2 top-1/2 h-[80vw] max-h-[320px] w-[80vw] max-w-[320px] -translate-x-1/2 -translate-y-1/2 rounded-[24px] border-[3px] border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]"
        ></div>
      </div>

      <button
        :class="[
          'fixed left-4 top-[calc(env(safe-area-inset-top,0px)+16px)] z-[10001] flex h-12 w-12 items-center justify-center rounded-full border-0 p-0 shadow-elevation-1',
          showCheckInSuccess
            ? 'bg-transparent text-white shadow-none'
            : showScannerSurface
              ? 'bg-grey-100/60 text-white'
              : 'bg-white text-grey-100',
        ]"
        type="button"
        aria-label="Close scanner"
        @click="closeScanner"
      >
        <ion-icon :icon="closeOutline" class="text-[1.75rem]" aria-hidden="true"></ion-icon>
      </button>

      <div
        v-if="showCheckInSuccess"
        class="flex min-h-0 flex-1 flex-col overflow-y-auto bg-scanner-success px-5 pb-[calc(env(safe-area-inset-bottom,0px)+24px)] pt-[calc(env(safe-area-inset-top,0px)+88px)]"
      >
        <div
          class="flex min-h-0 flex-1 flex-col items-center justify-center gap-7 text-center text-white"
        >
          <ScannerSuccessAnimation />

          <div class="flex w-full max-w-[360px] flex-col items-center gap-8">
            <DsHeading
              :level="1"
              tag="h1"
              class="w-full break-words text-h5-bold font-black tracking-normal text-white"
            >
              <template v-if="checkInResult?.alreadyCheckedIn">
                {{ checkInMilestoneText }}
              </template>
              <template v-else>
                You have checked in for
                <span :class="{ 'whitespace-nowrap': keepMilestoneNameOnOneLine }">
                  {{ checkInMilestoneName }}
                </span>
              </template>
            </DsHeading>
            <div v-if="showCheckInDetails" class="flex w-full flex-col items-center gap-1">
              <DsBody tag="p" variant="bold" class="w-full break-words text-white">
                {{ checkInRecordedAt }}
              </DsBody>
              <DsBody tag="p" variant="bold" class="w-full break-words text-white">
                {{ checkInPersonName }}
              </DsBody>
            </div>
          </div>
        </div>

        <DsButton
          variant="secondary"
          size="56"
          shape="pill"
          class="!flex !h-14 !w-full !items-center !justify-center !rounded-full !bg-white !px-6 !text-label-medium !font-semibold !text-scanner-success !shadow-elevation-1"
          @click="closeScanner"
        >
          Done
        </DsButton>
      </div>

      <div
        v-if="isScanMode && !showCheckInSuccess && !resolvingScanLink"
        :class="[
          'flex flex-col items-center px-5 text-center',
          showScannerSurface
            ? 'fixed left-0 top-[calc(50%_-_270px)] z-[10000] w-full'
            : 'pt-[calc(env(safe-area-inset-top,0px)+92px)]',
        ]"
      >
        <h1
          :class="[
            'w-full text-display-lg font-black',
            showScannerSurface ? 'text-white' : 'text-grey-100',
          ]"
        >
          {{ isBmtEntry ? 'Scan QR code' : 'Scan QR Code' }}
        </h1>
        <p
          :class="[
            'mt-2 w-full max-w-[320px] text-body font-semibold',
            showScannerSurface ? 'text-white/85' : 'text-grey-70',
          ]"
        >
          <template v-if="isBmtEntry">
            Point at the BMT QR code. It's on the screen at the venue.
          </template>
          <template v-else>
            Place the QR code inside the frame. We will open the right page automatically.
          </template>
        </p>
      </div>

      <!--
        A scanned link is being resolved, so none of the scanner's own furniture
        belongs on screen. The member pointed their phone's CAMERA at a code and
        expects to land somewhere — they never asked for the in-app scanner, and
        for most members it is not even a released feature. Showing "Ready to scan
        a HOGC One QR code" for the length of a network round trip reads as the
        wrong screen appearing and then vanishing.
      -->
      <div
        v-if="resolvingScanLink && !showCheckInSuccess"
        class="flex flex-1 flex-col items-center justify-center gap-4 px-5"
      >
        <Spinner size="lg" label="Opening your link" class="text-grey-60" />
      </div>

      <div
        v-if="isScanMode && !showScannerSurface && !showCheckInSuccess && !resolvingScanLink"
        class="flex flex-1 flex-col items-center justify-center px-5 pb-[calc(env(safe-area-inset-bottom,0px)+112px)] pt-8"
      >
        <div class="flex w-full max-w-[340px] flex-col items-center gap-6">
          <DsCard
            padding="lg"
            class="flex h-[260px] w-[260px] flex-col items-center justify-center gap-3 border-4 border-dashed border-grey-20 bg-white"
          >
            <DsIcon name="qr-code" size="xl" class="text-grey-40" />
            <div class="px-4 text-center text-body-small font-semibold text-grey-70">
              Ready to scan a HOGC One QR code
            </div>
          </DsCard>

          <Spinner
            v-if="isDispatching && !resultMessage"
            size="lg"
            label="Processing scan"
            class="text-grey-60"
          />

          <DsAlert
            v-if="resultMessage"
            class="w-full"
            :variant="resultSuccess ? 'success' : 'error'"
            :title="resultSuccess ? 'Scan processed' : 'Scan failed'"
          >
            {{ resultMessage }}
          </DsAlert>

          <DsButton
            v-if="scannerSupported"
            variant="secondary"
            size="56"
            shape="pill"
            class="w-full"
            :loading="isDispatching"
            :disabled="isDispatching"
            @click="scan"
          >
            Start Scanner
          </DsButton>

          <!-- Not supported message -->
          <DsAlert v-if="notSupported" class="w-full" variant="info" title="Native device required">
            Open this on the HOGC One mobile app to use the camera.
          </DsAlert>
        </div>
      </div>

      <div
        v-if="isQrMode && !showCheckInSuccess"
        class="flex min-h-0 flex-1 flex-col px-5 pb-[calc(env(safe-area-inset-bottom,0px)+112px)] pt-[calc(env(safe-area-inset-top,0px)+92px)]"
      >
        <h1 class="text-center text-display-lg font-black text-grey-100">My QR Code</h1>

        <div class="mt-8 flex min-h-0 flex-1 flex-col items-center justify-center gap-5">
          <Spinner v-if="profileLoading" size="lg" label="Loading QR code" class="text-grey-60" />

          <div v-else-if="personalId" class="flex w-full max-w-[350px] flex-col items-center gap-7">
            <div class="rounded-[24px] bg-white p-4 shadow-elevation-1">
              <qrcode-vue :value="personalId" :size="qrCodeSize" level="H" />
            </div>
            <div class="text-center">
              <div class="text-h5 font-semibold text-grey-100">Personal ID</div>
              <div class="mt-2 break-all text-h5-bold font-black text-grey-100">
                {{ personalId }}
              </div>
            </div>
          </div>

          <DsAlert
            v-else
            class="w-full"
            :variant="qrLoadError ? 'error' : 'info'"
            :title="qrMessageTitle"
          >
            {{ qrLoadError || qrInfoMessage || 'Complete your QR code setup from My QR Code.' }}
          </DsAlert>

          <DsButton
            v-if="!personalId"
            variant="primary"
            size="56"
            shape="pill"
            class="!flex !h-14 !w-full !max-w-[340px] !items-center !justify-center !rounded-full !bg-grey-100 !px-6 !text-label-medium !font-semibold !text-white !shadow-elevation-1"
            @click="openFullQrPage"
          >
            {{ qrActionLabel }}
          </DsButton>
        </div>
      </div>

      <!-- BMT check-in (`?from=bmt` only): which day this scan is for, and what to
           do if the camera fails. It takes the place of the Scan / QR Code switch,
           which has nothing to offer a trainee checking in. -->
      <div
        v-if="isBmtEntry && isScanMode && !showCheckInSuccess && !resolvingScanLink"
        class="fixed bottom-0 left-1/2 z-[10000] w-full max-w-[430px] -translate-x-1/2 rounded-t-[24px] bg-white px-5 pb-[calc(env(safe-area-inset-bottom,0px)+20px)] pt-5 text-ink"
        data-testid="bmt-scanner-panel"
      >
        <template v-if="bmtScanDay">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="text-text-body-small font-semibold text-grey-400">
                {{ bmtScanDayLine }}
              </div>
              <div class="text-text-subheading mt-0.5 font-bold" data-testid="bmt-scanner-topics">
                {{ bmtScanTopics }}
              </div>
            </div>
            <span
              v-if="bmtScanChip"
              class="text-text-body-small shrink-0 rounded-full px-2.5 py-1 font-semibold"
              :class="
                bmtScanChip.open
                  ? 'bg-status-successLite text-status-successInk'
                  : 'bg-brown-50 text-grey-400'
              "
              data-testid="bmt-scanner-chip"
            >
              {{ bmtScanChip.text }}
            </span>
          </div>
        </template>
        <p
          class="text-text-body-small m-0 text-grey-400"
          :class="{ 'mt-3 border-0 border-t border-solid border-brown-50 pt-3': bmtScanDay }"
        >
          Camera not working? Ask a BMT team member to mark you present.
        </p>
      </div>

      <div
        v-if="!showCheckInSuccess && !resolvingScanLink && !isBmtEntry"
        class="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+16px)] left-1/2 z-[10000] w-[90%] max-w-[430px] -translate-x-1/2"
        data-disable-swipe-navigation="true"
      >
        <div class="relative">
          <DsBottomNav
            :items="scannerNavItems"
            :model-value="activeMode"
            aria-label="Scanner mode"
            :fixed="false"
            :safe-area="false"
            :drag-enabled="false"
            class="w-full text-[#191817] [&>span]:!bg-[#fec629] [&_[data-bottom-nav-icon]]:!text-[#191817] [&_button]:!text-[#191817]"
            @select="selectScannerMode"
          >
            <template #icon="{ item }">
              <ion-icon
                :icon="scannerModeIcon(item.value)"
                class="h-5 w-5 text-[#191817] [--ionicon-stroke-width:44px]"
                aria-hidden="true"
              ></ion-icon>
            </template>
          </DsBottomNav>
        </div>
      </div>
    </div>

    <LinkCgModal v-model="showLinkCgModal" @linked="onCgLinked" @cancel="onCgCancelled" />
  </master-layout>
</template>

<script>
import { mapState } from 'vuex';
import dayjs from 'dayjs';
import {
  Alert as DsAlert,
  Body as DsBody,
  BottomNav as DsBottomNav,
  Button as DsButton,
  Card as DsCard,
  Heading as DsHeading,
  Icon as DsIcon,
  Spinner,
  useToast,
} from '@team-hogc/hogc-one-design-system';
import QrcodeVue from 'qrcode.vue';
import { closeOutline, qrCodeOutline, scanOutline } from 'ionicons/icons';
import { startScan, stopScan, isSupported } from '@/utils/qr-scanner';
import { isPlaceholderScannerSupported } from '@/utils/qr-scanner-placeholder';
import { handleQrResult } from '@/utils/qr-handler';
import { scannerPayloadFromRouteQuery } from '@/utils/scan-route';
import { useIonRouter } from '@ionic/vue';
import { buildScannerDisplayMessage } from '@/utils/scanner-display-message';
import { preloadScannerSuccessAnimation } from '@/utils/scanner-success-animation';
import ScannerSuccessAnimation from '@/components/Scanner/ScannerSuccessAnimation.vue';
import LinkCgModal from '@/components/common/LinkCgModal.vue';
import { BMT_SCANNER_ENTRY } from '@/utils/scanner-access';
import {
  checkinLabel,
  formatDayDate,
  formatTime,
  nextDay,
  openCheckinDay,
  topicsText,
} from '@/utils/bmt-display';

const SCANNER_TOAST_VARIANTS = new Set(['success', 'error', 'warning', 'info']);
const SCANNER_FALLBACK_ROUTE = '/tabs/home';
const BLOCKED_SCANNER_RETURN_PATHS = new Set([
  '/',
  '/login',
  '/pre-sign-in',
  '/pre-loading',
  '/register',
  '/registration',
  '/scanner',
  '/success-launch',
  // Pages that drive the camera themselves. Returning the scanner to one of
  // these lands on a second scanner surface — a white box behind an "Open
  // Scanner" button — which reads as a broken close. Falling back to home is
  // both safer and what the member expects from an X.
  '/photodrop',
  '/receive-photos',
  '/photographer-app',
]);
const BLOCKED_SCANNER_RETURN_PREFIXES = [
  '/scanner/',
  '/scan/',
  '/photodrop/',
  '/receive-photos/',
  '/photographer-app/',
];

export default {
  components: {
    DsAlert,
    DsBody,
    DsBottomNav,
    DsButton,
    DsCard,
    DsHeading,
    DsIcon,
    LinkCgModal,
    QrcodeVue,
    ScannerSuccessAnimation,
    Spinner,
  },
  setup() {
    // useIonRouter() supports 'root' navigation, which resets the stack to the
    // target. The injected navManager only does .back(), which cannot reach a
    // tabs route this standalone page was never pushed from — that no-op left
    // the close button dead on device.
    return { ionNav: useIonRouter() };
  },
  data() {
    return {
      activeMode: 'scan',
      isScanning: false,
      resultMessage: null,
      resultSuccess: false,
      checkInResult: null,
      isDispatching: false,
      // True from the moment we know this page was opened BY a scanned link, until
      // that link has been resolved. Set before any await in ionViewWillEnter so
      // the scanner surface never renders even for one frame — which is the whole
      // point; a flag set after an await still flashes.
      resolvingScanLink: false,
      scannerSupported: false,
      notSupported: false,
      isWebScannerFallback: false,
      qrLoadError: null,
      qrInfoMessage: null,
      viewportWidth: 390,
      showLinkCgModal: false,
      pendingCgScan: null,
      closeOutline,
    };
  },
  computed: {
    ...mapState('profile', {
      personalId: state => state.personalId,
      profileLoading: state => state.loading,
    }),
    isScanMode() {
      return this.activeMode === 'scan';
    },
    /** Opened by BMT's "Check in" (`?from=bmt`). Nothing else changes the scanner's face. */
    isBmtEntry() {
      return this.$route?.query?.from === BMT_SCANNER_ENTRY;
    },
    bmtMe() {
      return this.$store.state.bmt?.me || null;
    },
    /** Today's open day; failing that, the next day, so the panel still says when. */
    bmtScanDay() {
      if (!this.isBmtEntry || !this.bmtMe) return null;
      return openCheckinDay(this.bmtMe) || nextDay(this.bmtMe.days || [], this.bmtMe.serverTime);
    },
    bmtScanDayLine() {
      const day = this.bmtScanDay;
      // L4: which BMT and which day this scan is for.
      return [this.bmtMe?.enrolment?.label, formatDayDate(day?.date), day?.venue]
        .filter(Boolean)
        .join(' · ');
    },
    bmtScanTopics() {
      return topicsText(this.bmtScanDay);
    },
    bmtScanChip() {
      const checkin = this.bmtScanDay?.checkin;
      if (checkin?.state === 'open') {
        const closes = formatTime(checkin.closesAt);
        return { open: true, text: closes ? `Open till ${closes}` : 'Check-in open' };
      }
      const label = checkinLabel(checkin);
      return label.text ? { open: false, text: label.text } : null;
    },
    isQrMode() {
      return this.activeMode === 'qr';
    },
    showCheckInSuccess() {
      return Boolean(this.checkInResult);
    },
    showCheckInDetails() {
      return !this.checkInResult?.alreadyCheckedIn;
    },
    scannerNavItems() {
      return [
        { value: 'scan', label: 'Scan' },
        { value: 'qr', label: 'QR Code' },
      ];
    },
    showScannerSurface() {
      return (
        this.isScanMode &&
        !this.showCheckInSuccess &&
        !this.resultMessage &&
        (this.isScanning || this.isWebScannerFallback)
      );
    },
    checkInPersonName() {
      const member = this.checkInResult?.member || {};
      return (
        this.checkInResult?.fullName ||
        this.checkInResult?.name ||
        this.checkInResult?.memberName ||
        [member.name, member.surname].filter(Boolean).join(' ') ||
        'You'
      );
    },
    checkInMilestoneName() {
      const checkIn = this.checkInResult || {};
      const session = this.checkInResult?.session || {};
      const fallbackSessionId = this.checkInResult?.courseSessionId || session.id;
      return (
        checkIn.milestoneName ||
        checkIn.trainingName ||
        session.milestoneName ||
        session.trainingName ||
        session.title ||
        [session.trackName, session.syllabusName].filter(Boolean).join(' - ') ||
        (fallbackSessionId ? `Session ${fallbackSessionId}` : 'Milestone')
      );
    },
    checkInMilestoneText() {
      if (this.checkInResult?.alreadyCheckedIn) return 'You have already checked in.';
      return `You have checked in for ${this.checkInMilestoneName}`;
    },
    keepMilestoneNameOnOneLine() {
      return this.checkInMilestoneName.length <= 32;
    },
    checkInRecordedAt() {
      return this.formatDateTime(this.checkInResult?.checkedInAt || this.checkInResult?.createdAt);
    },
    qrMessageTitle() {
      if (this.qrLoadError) return 'Unable to load QR code';
      if (this.qrInfoMessage) return 'Log in required';
      return 'QR code details needed';
    },
    hasAccessToken() {
      return Boolean(this.$store.state.user?.token);
    },
    qrActionLabel() {
      if (!this.hasAccessToken) return 'Log In';
      return 'Set Up QR Code';
    },
    qrCodeSize() {
      const availableWidth = Math.max(this.viewportWidth - 80, 240);
      return Math.min(300, availableWidth);
    },
  },
  mounted() {
    this.updateViewportWidth();
    window.addEventListener('resize', this.updateViewportWidth);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateViewportWidth);
  },
  async ionViewWillEnter() {
    this.resetState();
    preloadScannerSuccessAnimation();

    // Resolved BEFORE the awaits below, and it is synchronous, so the flag is set
    // in the same tick the page mounts. Reading it after `await isSupported()`
    // would leave a frame or two where the scanner surface renders — which is
    // exactly the flash this removes.
    const scannerPayload = scannerPayloadFromRouteQuery(this.$route.query);
    this.resolvingScanLink = !!scannerPayload;

    const supported = await isSupported();
    this.scannerSupported = supported;
    this.isWebScannerFallback = !supported && isPlaceholderScannerSupported();
    this.notSupported = !supported && !this.isWebScannerFallback;

    if (scannerPayload) {
      try {
        await this.processScanResult({ type: 'scanner', scanner: scannerPayload });
      } finally {
        // Cleared whatever happened. On success the page has already been replaced
        // and this is moot; on FAILURE the member must get the scanner's normal UI
        // back, or they are left staring at a spinner with the error toast gone.
        this.resolvingScanLink = false;
      }
      return;
    }

    if (this.isBmtEntry) this.loadBmtDay();

    if (supported) {
      this.$nextTick(() => this.scan());
    }
  },
  ionViewWillLeave() {
    this.cancelScan();
  },
  methods: {
    /** The day the BMT panel describes. A failed read only leaves the panel without it. */
    loadBmtDay() {
      Promise.resolve(this.$store.dispatch('bmt/fetchMe', { ifStale: true })).catch(() => {});
    },
    updateViewportWidth() {
      this.viewportWidth = window.innerWidth || 390;
    },
    scannerModeIcon(mode) {
      return mode === 'scan' ? scanOutline : qrCodeOutline;
    },
    async selectScannerMode({ value }) {
      if (value === 'scan') {
        await this.showScanMode();
        return;
      }

      await this.showQrMode();
    },
    async showScanMode() {
      if (this.isScanMode) return;

      this.activeMode = 'scan';
      this.resultMessage = null;
      this.resultSuccess = false;
      this.checkInResult = null;

      if (this.scannerSupported) {
        this.$nextTick(() => this.scan());
      }
    },
    async showQrMode() {
      if (this.isQrMode) return;

      await this.cancelScan();
      this.activeMode = 'qr';
      this.resultMessage = null;
      this.resultSuccess = false;
      this.checkInResult = null;
      await this.loadProfileForQr();
    },
    openFullQrPage() {
      this.$router.push(this.hasAccessToken ? '/my-qr' : '/login');
    },
    async closeScanner() {
      await this.cancelScan();

      const returnPath = this.getScannerReturnPath();

      // Pop the scanner off the stack when there is a valid page beneath it: the
      // scanner slides off to the RIGHT and reveals that page, matching how
      // calendar, service highlights and the notifications drawer close. This is
      // a stack pop (useIonRouter().back), not a navigate — navigating to a tab
      // route always plays the tab transition (slide-up), which is the wrong
      // feel for a close. useIonRouter().back() is the app-wide standard; only
      // the injected navManager.back() was unreliable.
      if (returnPath && this.ionNav?.canGoBack?.()) {
        this.ionNav.back();
        return;
      }

      // Nothing safe to pop to — deep-linked straight in, or the page beneath is
      // another camera surface (filtered out by getScannerReturnPath). Navigate
      // to the resolved target instead; reliable, if without the pop animation.
      const targetPath = returnPath || SCANNER_FALLBACK_ROUTE;
      try {
        if (this.ionNav?.navigate) {
          await this.ionNav.navigate(targetPath, 'back', 'replace');
          return;
        }
      } catch (error) {
        console.warn('[Scanner] Ionic navigation failed, falling back:', error?.message);
      }
      await this.$router.replace(targetPath);
    },
    getScannerReturnPath() {
      const candidates = [
        this.$router?.options?.history?.state?.back,
        window.history?.state?.back,
        localStorage.getItem('route:previous'),
      ];

      return candidates
        .map(routePath => this.normalizeScannerReturnPath(routePath))
        .find(routePath => this.isAllowedScannerReturnPath(routePath));
    },
    normalizeScannerReturnPath(routePath) {
      if (typeof routePath !== 'string' || !routePath.trim()) return null;

      const path = routePath.trim().startsWith('/') ? routePath.trim() : `/${routePath.trim()}`;

      try {
        const url = new URL(path, window.location.origin);
        return `${url.pathname}${url.search}${url.hash}`;
      } catch {
        return null;
      }
    },
    isAllowedScannerReturnPath(routePath) {
      if (!routePath) return false;

      const resolvedRoute = this.$router?.resolve?.(routePath);
      if (!resolvedRoute?.matched?.length) return false;

      const pathname = resolvedRoute.path || this.normalizeScannerReturnPath(routePath);
      if (!pathname) return false;
      if (BLOCKED_SCANNER_RETURN_PATHS.has(pathname)) return false;

      return !BLOCKED_SCANNER_RETURN_PREFIXES.some(prefix => pathname.startsWith(prefix));
    },
    resetState() {
      this.activeMode = 'scan';
      this.resolvingScanLink = false;
      this.isScanning = false;
      this.resultMessage = null;
      this.resultSuccess = false;
      this.checkInResult = null;
      this.isDispatching = false;
      this.qrLoadError = null;
      this.qrInfoMessage = null;
    },
    getScanErrorMessage(error) {
      if (error.response?.status === 401) {
        return 'Please log in again to scan this QR code.';
      }

      if (error.response?.data) {
        return buildScannerDisplayMessage(
          error.response.data,
          error.message || 'Failed to process QR code.',
          {
            includeCode: true,
          },
        );
      }

      return error.response?.data?.message || error.message || 'Failed to process QR code.';
    },
    async showScanToast(type, message) {
      const variant = SCANNER_TOAST_VARIANTS.has(type) ? type : 'info';
      const toast = useToast();
      await toast.show({
        message,
        variant,
        position: 'bottom',
        bottomNav: true,
        duration: type === 'error' ? 6000 : 3000,
      });
    },
    getScannerPayload(data) {
      if (data?.type === 'scanner' && data.scanner) return data.scanner;
      if (data?.type && data?.action && data?.ref) return data;
      return null;
    },
    isCheckInScan(data) {
      const scannerPayload = this.getScannerPayload(data);
      return scannerPayload?.type === 'milestones' && scannerPayload?.action === 'checkin';
    },
    getCheckInFromResult(data, result, options = {}) {
      const scannerPayload = this.getScannerPayload(data) || {};
      const responseData = result?.response?.data || {};
      const checkIn =
        responseData.checkIn ||
        responseData.checkin ||
        responseData.attendance ||
        (responseData.memberId ? responseData : {});

      return {
        ...checkIn,
        alreadyCheckedIn:
          checkIn.alreadyCheckedIn === true ||
          responseData.alreadyCheckedIn === true ||
          options.alreadyCheckedIn === true,
        checkedInAt:
          checkIn.checkedInAt ||
          responseData.checkedInAt ||
          checkIn.createdAt ||
          responseData.createdAt ||
          new Date().toISOString(),
        courseSessionId:
          checkIn.courseSessionId || responseData.courseSessionId || scannerPayload.ref || null,
        session: checkIn.session || responseData.session || {},
      };
    },
    isAlreadyCheckedInResult(result) {
      const response = result?.response || {};
      const display = response.display || {};
      const searchableText = [
        response.status,
        response.code,
        response.message,
        display.title,
        display.message,
        result?.message,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return (
        searchableText.includes('already checked in') ||
        searchableText.includes('already been recorded')
      );
    },
    formatDateTime(value) {
      if (!value) return 'Just now';
      const parsedDate = dayjs(value);
      return parsedDate.isValid() ? parsedDate.format('D MMM YYYY, h:mm A') : 'Just now';
    },
    async loadProfileForQr() {
      if (this.profileLoading || this.personalId) return;

      if (!this.hasAccessToken) {
        this.qrInfoMessage = 'Log in to view your personal QR code.';
        return;
      }

      try {
        this.qrLoadError = null;
        this.qrInfoMessage = null;
        await this.$store.dispatch('profile/fetchProfile');
      } catch (error) {
        if (error.response?.status === 401) {
          this.qrInfoMessage = 'Log in to view your personal QR code.';
          return;
        }

        this.qrLoadError = error.message || 'Failed to load your QR code.';
      }
    },
    async scan() {
      if (!this.scannerSupported) return;

      this.resultMessage = null;
      this.checkInResult = null;
      this.isScanning = true;

      try {
        const data = await startScan();

        if (!data) {
          this.isScanning = false;
          return;
        }

        await this.processScanResult(data);
      } catch (error) {
        const message = error.message || 'Failed to scan QR code.';
        this.resultMessage = null;
        this.resultSuccess = false;
        this.showScanToast('error', message);
      } finally {
        this.isScanning = false;
      }
    },
    async processScanResult(data) {
      this.isDispatching = true;

      try {
        const result = await handleQrResult(data, { router: this.$router, store: this.$store });

        // The member has no CG on record and the scan needs one. Hold the
        // pending payload and prompt — onCgLinked re-runs the same scan.
        if (result.needsCgLink) {
          this.pendingCgScan = result.payload ? { type: 'scanner', scanner: result.payload } : data;
          this.showLinkCgModal = true;
          return;
        }

        // The lane already moved on to its own result screen (BMT check-in).
        if (result.navigated) return;

        const alreadyCheckedIn = this.isCheckInScan(data) && this.isAlreadyCheckedInResult(result);
        this.resultSuccess = result.success || alreadyCheckedIn;

        if (!result.success && !alreadyCheckedIn) {
          this.resultMessage = null;
          this.showScanToast('error', result.message);
          return;
        }

        this.resultMessage = result.message;

        if (this.isCheckInScan(data)) {
          this.checkInResult = this.getCheckInFromResult(data, result, { alreadyCheckedIn });
          this.resultMessage = null;
          return;
        }

        this.showScanToast('success', result.message);
      } catch (error) {
        const message = this.getScanErrorMessage(error);
        this.resultMessage = null;
        this.resultSuccess = false;
        this.showScanToast('error', message);
      } finally {
        this.isDispatching = false;
      }
    },
    async onCgLinked() {
      const pending = this.pendingCgScan;
      this.pendingCgScan = null;
      if (pending) await this.processScanResult(pending);
    },
    onCgCancelled() {
      this.pendingCgScan = null;
      this.resultSuccess = false;
      this.showScanToast('error', 'Add your Connect Group to unlock this lesson.');
    },
    async cancelScan() {
      if (this.scannerSupported || this.isScanning) {
        await stopScan();
      }

      this.isScanning = false;
    },
  },
};
</script>

<style scoped>
:global(body.scanner-active),
:global(body.scanner-active ion-app),
:global(body.scanner-active ion-app ion-router-outlet),
:global(body.scanner-active ion-page),
:global(body.scanner-active ion-content),
:global(body.scanner-active ion-content::part(background)),
:global(body.scanner-active .scanner-page),
:global(body.scanner-active .scroll-content) {
  --background: transparent !important;
  --ion-background-color: transparent !important;
  background: transparent !important;
  background-color: transparent !important;
}

:global(ion-app.scanner-active) {
  --ion-background-color: transparent !important;
}
</style>
